import { useMemo, type ReactNode } from "react";
import styles from "./LinkifyText.module.scss";

/**
 * URL detection regex - Matches common web URLs.
 *
 * Supports: Required http(s)://, optional www, multiple subdomains, domains with optional TLD, multi-part TLDs, port numbers, paths/query strings
 * Examples: https://example.com, https://www.example.com, https://app.higharc.com, https://app.dev.higharc.com, https://example.co.uk, https://localhost:3000, https://api.example.com:8080/v1/users?id=123
 *
 */
export const URL_REGEX =
  /\bhttps?:\/\/(www\.)?([A-Za-z0-9-]+\.)*([A-Za-z0-9-]+)(\.[A-Za-z]{2,})?(\.[A-Za-z]{2,})?(:[0-9]{1,5})?(\/[^\s<>]*)?\b/gi;
const MAX_URL_LENGTH = 25;

/**
 * Convert plain text containing URLs into React nodes with clickable links
 *
 * @param text - The plain text to convert
 * @returns React nodes with URLs wrapped in <a> tags
 *
 * @example
 * linkifyTextInReact("Check example.com and www.github.com")
 * // returns: <span className={styles.wrapper}>
 * //   Check{" "}
 * //   <a href="https://example.com">example.com</a>
 * //   {" and "}
 * //   <a href="https://www.github.com">www.github.com</a>
 * // </span>
 *
 */

interface LinkifyTextProps {
  text?: ReactNode;
}

export function LinkifyText({ text }: LinkifyTextProps): ReactNode {
  return useMemo(() => {
    if (typeof text !== "string" || !text) {
      return text;
    }
    const output: ReactNode[] = [];
    let indexAfterURL = 0;
    let key = 0;

    for (const match of text.matchAll(URL_REGEX)) {
      const index = match.index;

      // Add any plain text before the URL
      if (index > indexAfterURL) {
        output.push(text.slice(indexAfterURL, index));
      }

      const url = match[0];

      output.push(
        <a
          key={key++}
          href={url}
          aria-label={url}
          target="_blank"
          rel="noopener noreferrer"
          title={url}
          className={styles.link}
        >
          {url.length > MAX_URL_LENGTH ? url.slice(0, MAX_URL_LENGTH) + "..." : url}
        </a>,
      );

      indexAfterURL = index + url.length;
    }

    // Add any remaining text after the last URL
    if (indexAfterURL < text.length) {
      output.push(text.slice(indexAfterURL));
    }

    return <span className={styles.wrapper}>{output}</span>;
  }, [text]);
}
